function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
import { jsx as _jsx } from "react/jsx-runtime";
import React from 'react';
import PropTypes from 'prop-types';
import withStyles from 'isomorphic-style-loader/withStyles';
import { injectIntl } from 'react-intl';
import moment from 'moment';
import intlShape from 'shapes/intlShape';
import { formatTimestamp, formatDate } from 'helpers/datetime';
import { saveAsCSV } from 'helpers/csv';
import { calculateRelatedValueForBGDataReport, aggregateGroupedRelatedValues } from 'helpers/relatedData';
import Table from 'components/Table';
import { isManualReading } from 'helpers/externalDataSources';
import { translateLocation } from '../../helpers';
import { TimeSeriesResourcesHelper } from '../../helpers/TimeSeriesResourcesHelper';
import messages from '../../messages';
import styles from './PatientsBgDataReport.pcss';
var _React_Component;
class PatientsBgDataReport extends (_React_Component = React.Component) {
    componentDidUpdate(prevProps) {
        if (this.props.downloadCsv && !prevProps.downloadCsv) {
            this.saveAsCsv();
        }
        if (prevProps.locale !== this.props.locale && typeof Intl.DisplayNames === 'function') {
            this.CountriesNames = new Intl.DisplayNames([
                this.props.locale
            ], {
                type: 'region'
            });
            this.TimeSeriesResourcesHelper = new TimeSeriesResourcesHelper(this.props.intl, this.props.accountPatientProfile.locations, this.CountriesNames);
        }
    }
    get name() {
        const { accountPatientProfile, intl } = this.props;
        const reportName = intl.formatMessage(messages.buttons.PatientsBgData).replaceAll(' ', '_');
        const patientName = `${accountPatientProfile.firstName}_${accountPatientProfile.lastName}`;
        if (this.props.isMobileDisplayMode) {
            return `${reportName}_${patientName}_${formatDate(new Date()).replaceAll('.', '_')}`;
        }
        return `${reportName}_${patientName}_${new Date().toLocaleDateString().replaceAll('.', '_')}`;
    }
    get readingsRelatedValues() {
        const { conversion, intl, accountPatientProfile } = this.props;
        return this.readingsWithRelatedData.reduce((acc, reading)=>{
            const dateKey = formatTimestamp(reading.timestamp, 'L LTS');
            const result = {
                value: conversion.toDisplay(reading.value),
                // locations are only set for newCDA report base on custom settings
                // for gco user this column will be empty
                location: reading.locationLabelUID ? translateLocation(reading.locationLabelUID, accountPatientProfile.locations, this.CountriesNames) : null,
                mealMarker: intl.formatMessage(messages.labels.patientsBgDataReport.flags[reading.flags]),
                dataSource: isManualReading(reading) ? intl.formatMessage(messages.labels.patientsBgDataReport.manual) : intl.formatMessage(messages.labels.patientsBgDataReport.meter)
            };
            if (reading.relatedData) {
                const relatedData = calculateRelatedValueForBGDataReport(reading.relatedData);
                if (relatedData.medications !== undefined) {
                    result.medications = relatedData.medications;
                }
                if (relatedData.foodValue !== undefined) {
                    result.foodValue = relatedData.foodValue;
                }
            }
            acc[dateKey] = result;
            return acc;
        }, {});
    }
    get entities() {
        const timeSeriesResources = this.TimeSeriesResourcesHelper.translateToBgDataReportRelatedValues(this.props.timeSeriesResources);
        const groupedRelatedValues = aggregateGroupedRelatedValues(this.readingsRelatedValues, timeSeriesResources);
        const a = Object.entries(groupedRelatedValues).sort(([timeA], [timeB])=>moment.utc(timeA, 'L LTS').unix() - moment.utc(timeB, 'L LTS').unix()).map(([formattedTimeStamp, relatedValue], index)=>this.toRow(relatedValue, formattedTimeStamp, index));
        return a;
    }
    get header() {
        return this.schema.map((schemaItem)=>schemaItem.labelMessage);
    }
    get schema() {
        const { intl, conversion, carbsConversion } = this.props;
        return [
            {
                key: 'index',
                labelMessage: '#'
            },
            {
                key: 'time',
                labelMessage: intl.formatMessage(messages.labels.patientsBgDataReport.time)
            },
            {
                key: 'value',
                labelMessage: `${intl.formatMessage(messages.labels.patientsBgDataReport.value)} [${conversion.unitSymbol}]`
            },
            {
                key: 'mealMarker',
                labelMessage: intl.formatMessage(messages.labels.patientsBgDataReport.mealMarker)
            },
            {
                key: 'dataSource',
                labelMessage: `${intl.formatMessage(messages.labels.patientsBgDataReport.dataSource)}`
            },
            {
                key: 'note',
                labelMessage: `${intl.formatMessage(messages.labels.patientsBgDataReport.notes)}`
            },
            {
                key: 'activity',
                labelMessage: `${intl.formatMessage(messages.labels.patientsBgDataReport.activity)}`
            },
            {
                key: 'foodValue',
                labelMessage: `${intl.formatMessage(messages.labels.patientsBgDataReport.foodValue)} [${carbsConversion.unitSymbol}]`
            },
            {
                key: 'medications',
                labelMessage: intl.formatMessage(messages.labels.patientsBgDataReport.medications)
            },
            {
                key: 'location',
                labelMessage: intl.formatMessage(messages.labels.patientsBgDataReport.location)
            }
        ];
    }
    get readingsWithRelatedData() {
        const { readings, relatedData } = this.props;
        return readings.map((reading)=>{
            const findRelatedData = relatedData && relatedData.find((item)=>item.readingId === reading.externalId);
            if (findRelatedData) {
                reading.relatedData = findRelatedData;
            }
            return reading;
        });
    }
    toRow(relatedValue, formatedTimeStamp, index) {
        const { carbsConversion } = this.props;
        return {
            index: index + 1,
            timestamp: moment.utc(formatedTimeStamp, 'L LTS', true).unix(),
            time: formatedTimeStamp,
            value: relatedValue.value || '',
            medications: relatedValue.medications || '',
            note: relatedValue.note || '',
            activity: relatedValue.activity || '',
            foodValue: carbsConversion.toDisplay(relatedValue.foodValue) || '',
            mealMarker: relatedValue.mealMarker || '',
            dataSource: relatedValue.dataSource || '',
            location: relatedValue.location || ''
        };
    }
    saveAsCsv() {
        const schemaKeys = this.schema.map((item)=>item.key);
        const csvRows = this.entities.map((entry)=>schemaKeys.map((key)=>entry[key]));
        saveAsCSV(this.name, this.header, csvRows);
    }
    render() {
        return /*#__PURE__*/ _jsx("div", {
            className: styles.patientsBgDataReport,
            children: /*#__PURE__*/ _jsx(Table, {
                idKey: "index",
                schema: this.schema,
                entities: this.entities,
                perPage: 25,
                isPerPageOff: true
            })
        });
    }
    /**
   *
   */ constructor(props){
        super(props);
        this.TimeSeriesResourcesHelper = new TimeSeriesResourcesHelper(props.intl, props.accountPatientProfile.locations, []);
        if (typeof Intl.DisplayNames === 'function') {
            this.CountriesNames = new Intl.DisplayNames([
                this.props.locale
            ], {
                type: 'region'
            });
            this.TimeSeriesResourcesHelper = new TimeSeriesResourcesHelper(props.intl, props.accountPatientProfile.locations, this.CountriesNames);
        }
    }
}
_define_property(PatientsBgDataReport, "propTypes", {
    // Explicit props
    conversion: PropTypes.object.isRequired,
    carbsConversion: PropTypes.object.isRequired,
    standards: PropTypes.shape({
        maxValue: PropTypes.number.isRequired,
        preMeal: PropTypes.shape({
            highThreshold: PropTypes.number.isRequired,
            lowThreshold: PropTypes.number.isRequired
        }),
        postMeal: PropTypes.shape({
            highThreshold: PropTypes.number.isRequired,
            lowThreshold: PropTypes.number.isRequired
        })
    }),
    readings: PropTypes.array,
    relatedData: PropTypes.array,
    timeSeriesResources: PropTypes.array,
    accountPatientProfile: PropTypes.object.isRequired,
    downloadCsv: PropTypes.bool,
    isMobileDisplayMode: PropTypes.bool,
    locale: PropTypes.string,
    // Implicit props
    intl: intlShape.isRequired
});
export default withStyles(styles)(injectIntl(PatientsBgDataReport));
